import {
  useMediaQuery,
  Icon,
  Flex,
  Text,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  RadioGroup,
  Radio,
  Stack,
} from '@chakra-ui/react';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Marker, MarkerDragEvent } from 'react-map-gl';

import { IconLocationPinStart, IconTrashbin } from 'assets/icons';
import { FormikCheckbox, FormikRadio } from 'components/Formik';
import Mapbox from 'components/Mapbox';
import DrawControl from 'components/Mapbox/DrawControl';
import JobRadius from 'components/Mapbox/JobRadius';
import { useDeleteCustomGeofence } from 'hooks/jobs/useDeleteCustomGeofence';

import { initialJobFormData } from '../schema';
import polgyonStyles from './polygonStyles';

interface IJobFormMapProps {
  values: typeof initialJobFormData;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  showMarker: boolean;
}

const JobFormMap: React.FC<IJobFormMapProps> = ({ values, setFieldValue, showMarker }) => {
  const { t } = useTranslation(['jobsTasks', 'common']);
  const drawRef = useRef<MapboxDraw | null>(null);

  const [viewport, setViewport] = useState({
    latitude: +(values.latitude ?? 0),
    longitude: +(values.longitude ?? 0),
    zoom: 13,
  });
  const [mapView, setMapView] = useState<'street' | 'satellite'>('street');
  const [isLargerThan768] = useMediaQuery(['(min-width: 768px)'], { ssr: false });

  const { mutateAsync: mutateAsyncDeleteCustomGeofence } = useDeleteCustomGeofence();

  useEffect(() => {
    setViewport((nextviewport: any) => ({
      ...nextviewport,
      latitude: +(values.latitude ?? 0),
      longitude: +(values.longitude ?? 0),
      zoom: showMarker ? (viewport.zoom === 0 ? 13 : viewport.zoom) : 0,
    }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.latitude, values.longitude, showMarker]);

  useEffect(() => {
    if (values.feature_collection?.type === 'FeatureCollection' && values.custom_geofence) {
      setTimeout(() => {
        if (drawRef.current) {
          drawRef.current.set(values.feature_collection);
          drawRef.current.changeMode('simple_select');

          const polygonIcon = document.querySelector('.mapbox-gl-draw_polygon');

          if (polygonIcon) {
            polygonIcon.setAttribute('disabled', '');
          }
        }
      }, 1000);
    }
  }, [values?.custom_geofence, values?.feature_collection]);

  const onCreate = useCallback(
    (e: { features: any }) => {
      let newFeatures = { ...values.geofence_features };

      for (const f of e.features) {
        newFeatures = f;
      }

      const polygonIcon = document.querySelector('.mapbox-gl-draw_polygon');

      if (polygonIcon) {
        polygonIcon.setAttribute('disabled', '');
      }

      setFieldValue('geofence_features', newFeatures);
      setFieldValue('feature_collection', {
        type: 'FeatureCollection',
        features: [newFeatures],
      });
    },
    [setFieldValue, values.geofence_features]
  );

  const onUpdate = useCallback(
    (e: { features: any }) => {
      let newFeatures = { ...values.geofence_features };

      for (const f of e.features) {
        newFeatures = f;
      }
      setFieldValue('geofence_features', newFeatures);
      setFieldValue('feature_collection', {
        type: 'FeatureCollection',
        features: [newFeatures],
      });
    },

    [setFieldValue, values.geofence_features]
  );

  const onDelete = useCallback(() => {
    const allFeatures = drawRef.current?.getAll();

    const polygonIcon = document.querySelector('.mapbox-gl-draw_polygon');
    if (polygonIcon) {
      polygonIcon?.removeAttribute('disabled');
    }

    if (!allFeatures?.features?.length) return;

    drawRef.current?.deleteAll();

    setFieldValue('geofence_features', {});
    setFieldValue('feature_collection', {
      type: 'FeatureCollection',
      features: [],
    });

    if (values.id) {
      mutateAsyncDeleteCustomGeofence({ id: values.id });
    }
  }, [setFieldValue, values.id, mutateAsyncDeleteCustomGeofence]);

  const handleMarkerDragend = (
    event: MarkerDragEvent,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    const longitude = event.lngLat.lng;
    const latitude = event.lngLat.lat;

    setFieldValue('longitude', longitude);
    setFieldValue('latitude', latitude);
  };

  const handleMapViewChange = useCallback((nextValue: 'street' | 'satellite') => {
    setMapView(nextValue);
  }, []);

  return (
    <>
      <Text mb="2" color="gray.500">
        {t('jobsTasks:dragPinOnTheMapToRelocate')}
      </Text>
      <Mapbox
        {...viewport}
        style={{ width: '100%', height: isLargerThan768 ? '370px' : '300px' }}
        onMove={(evt: any) => {
          if (evt?.viewState) {
            const { latitude, longitude, zoom } = evt.viewState;
            setViewport({ latitude, longitude, zoom });
          }
        }}
        mapStyle={
          mapView === 'street' ? 'mapbox://styles/mapbox/streets-v11' : 'mapbox://styles/mapbox/satellite-streets-v9'
        }
      >
        <RadioGroup
          onChange={handleMapViewChange}
          value={mapView}
          position="absolute"
          top="10px"
          left="10px"
          p="1"
          px="2"
          bg="white"
          colorScheme="primary"
          fontWeight="semibold"
          borderRadius="lg"
        >
          <Stack direction="row" cursor="pointer">
            <Radio size="sm" value="street" mr="2">
              {t('common:street')}
            </Radio>
            <Radio size="sm" value="satellite">
              {t('common:satellite')}
            </Radio>
          </Stack>
        </RadioGroup>
        {values.requireGeofence.includes('yes') && values.custom_geofence === '1' ? (
          <>
            <DrawControl
              position="top-right"
              displayControlsDefault={false}
              controls={{
                polygon: true,
                // trash: true,
              }}
              defaultMode="draw_polygon"
              onCreate={onCreate}
              onUpdate={onUpdate}
              ref={drawRef}
              styles={polgyonStyles}
            />

            <Icon
              as={IconTrashbin}
              width="29px"
              height="29px"
              padding="8px"
              background="white"
              position="absolute"
              top="40px"
              right="10px"
              borderRadius="md"
              sx={{ '& path': { fill: 'black' } }}
              cursor="pointer"
              onClick={onDelete}
              boxShadow="0 0 0 2px rgb(0 0 0/10%)"
            />
          </>
        ) : null}
        {values.requireGeofence.includes('yes') && values.custom_geofence === '0' ? (
          <JobRadius radius={values.radius} latitude={values.latitude ?? 0} longitude={values.longitude ?? 0} />
        ) : null}
        <Marker
          longitude={values.longitude ?? 0}
          latitude={values.latitude ?? 0}
          offset={[0, -5]}
          draggable={true}
          onDragEnd={(event: MarkerDragEvent) => handleMarkerDragend(event, setFieldValue)}
        >
          <Icon as={IconLocationPinStart} fontSize="3xl" display={showMarker ? 'block' : 'none'} />
        </Marker>
      </Mapbox>
      <FormikCheckbox
        options={[{ label: 'Require Geofencing', value: 'yes' }]}
        name="requireGeofence"
        formControlProps={{ mt: 3 }}
        checkboxProps={{ colorScheme: 'secondary' }}
      />
      {values.requireGeofence.includes('yes') ? (
        <FormikRadio
          label="Select geofence types"
          options={[
            { label: 'Circular Geofence', value: '0' },
            { label: 'Custom Geofence (Polygon)', value: '1' },
          ]}
          radioProps={{
            colorScheme: 'secondary',
          }}
          name="custom_geofence"
        />
      ) : null}

      {values.requireGeofence.includes('yes') && values.custom_geofence === '0' ? (
        <>
          <Flex justifyContent="space-between" alignItems="center" mb="3">
            <Text as="label" textTransform="uppercase" fontSize="xs" fontWeight="600">
              {t('jobsTasks:distanceToClock(InMeters)')}
            </Text>
            <Text px="3" py="2" bg="gray.100" border="1px" borderColor="gray.300" borderRadius="md" fontWeight="600">
              {values.radius} {t('jobsTasks:meters')}
            </Text>
          </Flex>
          <Slider
            aria-label="slider-ex-5"
            colorScheme="primary"
            onChange={val => setFieldValue('radius', val)}
            min={100}
            max={2500}
            size="lg"
            name="radius"
            value={values.radius}
          >
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            <SliderThumb bg="primary.700" />
          </Slider>
          <Flex justifyContent="space-between" fontSize="xs" mt="2" color="gray.500">
            <Text>{t('jobsTasks:100')}</Text>
            <Text>{t('jobsTasks:2500')}</Text>
          </Flex>
        </>
      ) : null}
    </>
  );
};

export default JobFormMap;
